import {
  AutoAwesome as AgentIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  NotificationsActive as SignalIcon,
  People as PeopleIcon,
  Storefront as EngagedIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartTooltip,
  XAxis,
  YAxis,
} from "recharts";

import type {
  AccountStreamMatch,
  ActivityFeedItem,
  FeedEventType,
} from "../../types/accountIntelligence";
import {
  MOCK_ACCOUNT_STREAM_MATCHES,
  MOCK_ACTIVITY_FEED,
  MOCK_SIGNALS,
  MOCK_STREAMS,
  getICPLabel,
} from "./mockData";
import AccountStateChip from "./shared/AccountStateChip";
import AccountWorkspace from "./AccountWorkspace";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getLastSignalDate(match: AccountStreamMatch): Date | null {
  if (!match.signals.length) return null;
  return new Date(Math.max(...match.signals.map((s) => new Date(s.date).getTime())));
}

function daysBetween(a: Date, b: Date): number {
  return Math.floor((b.getTime() - a.getTime()) / 86400000);
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// ─── Activity timeline data ───────────────────────────────────────────────────

type Period = 14 | 30 | 60;

interface DayData {
  label: string;
  fullDate: string;
  dateStr: string;
  signals: number;
  opportunities: number;
  candidates: number;
  decisions: number;
}

// Synthetic events to fill older periods and make charts look realistic
const SYNTHETIC_EVENTS: Array<{ date: string; type: keyof Omit<DayData, "label" | "fullDate" | "dateStr"> }> = [
  { date: "2026-03-05", type: "signals" },
  { date: "2026-03-07", type: "candidates" },
  { date: "2026-03-09", type: "signals" },
  { date: "2026-03-12", type: "opportunities" },
  { date: "2026-03-14", type: "signals" },
  { date: "2026-03-16", type: "decisions" },
  { date: "2026-03-19", type: "signals" },
  { date: "2026-03-21", type: "candidates" },
  { date: "2026-03-24", type: "signals" },
  { date: "2026-03-25", type: "opportunities" },
  { date: "2026-03-27", type: "signals" },
  { date: "2026-03-30", type: "decisions" },
  { date: "2026-04-01", type: "signals" },
  { date: "2026-04-03", type: "candidates" },
  { date: "2026-04-06", type: "signals" },
  { date: "2026-04-07", type: "opportunities" },
  { date: "2026-04-09", type: "decisions" },
  { date: "2026-04-11", type: "signals" },
  { date: "2026-04-14", type: "candidates" },
  { date: "2026-04-16", type: "signals" },
  { date: "2026-04-17", type: "opportunities" },
  { date: "2026-04-19", type: "signals" },
  { date: "2026-04-21", type: "decisions" },
  { date: "2026-04-22", type: "signals" },
  { date: "2026-04-23", type: "candidates" },
  { date: "2026-04-24", type: "opportunities" },
  { date: "2026-04-27", type: "signals" },
  { date: "2026-04-29", type: "decisions" },
];

function buildActivityData(period: Period): DayData[] {
  const today = new Date("2026-05-04");

  return Array.from({ length: period }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (period - 1 - i));
    const dateStr = date.toISOString().split("T")[0];

    // Real signals from mock data
    const realSignals = MOCK_SIGNALS.filter((s) => s.date === dateStr).length;

    // Real feed events broken down by type
    const feedItems = MOCK_ACTIVITY_FEED.filter((f) => f.date.startsWith(dateStr));
    const realOpportunities = feedItems.filter((f) => f.type === "opportunity_identified").length;
    const realCandidates = feedItems.filter((f) => f.type === "new_candidate").length;
    const realDecisions = feedItems.filter(
      (f) => f.type === "decision_made" || f.type === "user_status_change",
    ).length;
    const feedSignals = feedItems.filter((f) => f.type === "signal_detected").length;

    // Synthetic supplementary events
    const synth = SYNTHETIC_EVENTS.filter((e) => e.date === dateStr);
    const synthSignals = synth.filter((e) => e.type === "signals").length;
    const synthOpportunities = synth.filter((e) => e.type === "opportunities").length;
    const synthCandidates = synth.filter((e) => e.type === "candidates").length;
    const synthDecisions = synth.filter((e) => e.type === "decisions").length;

    const showLabel =
      period === 14
        ? i % 2 === 0
        : period === 30
        ? i % 5 === 0
        : i % 9 === 0;

    const fullDate = date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });

    return {
      label: showLabel ? fullDate : "",
      fullDate,
      dateStr,
      signals: realSignals + feedSignals + synthSignals,
      opportunities: realOpportunities + synthOpportunities,
      candidates: realCandidates + synthCandidates,
      decisions: realDecisions + synthDecisions,
    };
  });
}

// ─── Feed event config ────────────────────────────────────────────────────────

const FEED_CONFIG: Record<FeedEventType, { color: string; label: string }> = {
  signal_detected: { color: "#f9a825", label: "Signal" },
  new_candidate: { color: "#9e9e9e", label: "Candidate" },
  opportunity_identified: { color: "#1976d2", label: "Opportunity" },
  user_status_change: { color: "#e65100", label: "Status" },
  decision_made: { color: "#2e7d32", label: "Decision" },
};

// ─── Account detail drawer ─────────────────────────────────────────────────────

const AccountDetailDrawer: React.FC<{ match: AccountStreamMatch | null; onClose: () => void }> = ({
  match,
  onClose,
}) => (
  <Drawer
    anchor="right"
    open={!!match}
    onClose={onClose}
    PaperProps={{
      sx: {
        width: { xs: "100vw", sm: 680 },
        backgroundColor: "background.paper",
        backgroundImage: "none",
      },
    }}
  >
    {match && (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <AccountWorkspace accountId={match.account.id} streamId={match.streamId} onBack={onClose} />
      </Box>
    )}
  </Drawer>
);

// ─── Metric card ──────────────────────────────────────────────────────────────

interface MetricCardProps {
  value: number;
  label: string;
  sublabel?: string;
  color: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({ value, label, sublabel, color, icon, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      flex: 1,
      minWidth: 140,
      border: "1px solid",
      borderColor: "divider",
      borderLeft: `3px solid ${color}`,
      borderRadius: 1.5,
      p: 2.25,
      cursor: onClick ? "pointer" : "default",
      backgroundColor: `${color}07`,
      "&:hover": onClick
        ? { backgroundColor: `${color}12`, borderColor: color }
        : {},
      transition: "background-color 0.15s, border-color 0.15s",
      position: "relative",
      overflow: "hidden",
    }}
  >
    {/* Background accent circle */}
    <Box
      sx={{
        position: "absolute",
        top: -16,
        right: -16,
        width: 80,
        height: 80,
        borderRadius: "50%",
        backgroundColor: `${color}12`,
        pointerEvents: "none",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        top: 12,
        right: 12,
        color,
        opacity: 0.7,
        "& svg": { fontSize: 20 },
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="h3"
      fontWeight={800}
      sx={{ color, lineHeight: 1, mb: 0.5, fontSize: "2.2rem" }}
    >
      {value}
    </Typography>
    <Typography variant="body2" fontWeight={600} color="text.primary" lineHeight={1.2}>
      {label}
    </Typography>
    {sublabel && (
      <Typography variant="caption" color="text.disabled" display="block" mt={0.25}>
        {sublabel}
      </Typography>
    )}
  </Paper>
);

// ─── Signal activity chart ────────────────────────────────────────────────────

// ─── Activity timeline component ─────────────────────────────────────────────

const SERIES = [
  { key: "signals" as const, label: "Signals", color: "#f9a825" },
  { key: "opportunities" as const, label: "Opportunities", color: "#1976d2" },
  { key: "candidates" as const, label: "Candidates", color: "#9e9e9e" },
  { key: "decisions" as const, label: "Decisions", color: "#2e7d32" },
];

const ActivityTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; fill: string }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + p.value, 0);
  if (total === 0) return null;
  return (
    <Box
      sx={{
        backgroundColor: "#252525",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 1,
        px: 1.75,
        py: 1.25,
        minWidth: 140,
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.75}>
        {label}
      </Typography>
      {payload
        .filter((p) => p.value > 0)
        .map((p) => (
          <Box key={p.name} sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.25 }}>
            <Typography variant="caption" sx={{ color: p.fill }}>
              {p.name}
            </Typography>
            <Typography variant="caption" fontWeight={700} sx={{ color: p.fill }}>
              {p.value}
            </Typography>
          </Box>
        ))}
      <Divider sx={{ my: 0.75 }} />
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="caption" color="text.secondary">Total</Typography>
        <Typography variant="caption" fontWeight={700} color="text.primary">{total}</Typography>
      </Box>
    </Box>
  );
};

const ActivityTimeline: React.FC = () => {
  const [period, setPeriod] = useState<Period>(30);
  const data = useMemo(() => buildActivityData(period), [period]);

  const totals = data.reduce(
    (acc, d) => ({
      signals: acc.signals + d.signals,
      opportunities: acc.opportunities + d.opportunities,
      candidates: acc.candidates + d.candidates,
      decisions: acc.decisions + d.decisions,
    }),
    { signals: 0, opportunities: 0, candidates: 0, decisions: 0 },
  );
  const grandTotal = Object.values(totals).reduce((a, b) => a + b, 0);

  // Compare to previous period for trend
  const prevData = useMemo(() => {
    const today = new Date("2026-05-04");
    return Array.from({ length: period }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - period - (period - 1 - i));
      const dateStr = date.toISOString().split("T")[0];
      return SYNTHETIC_EVENTS.filter((e) => e.date === dateStr).length;
    });
  }, [period]);
  const prevTotal = prevData.reduce((a, b) => a + b, 0);
  const trendPct = prevTotal > 0 ? Math.round(((grandTotal - prevTotal) / prevTotal) * 100) : null;

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        overflow: "hidden",
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "rgba(255,255,255,0.015)",
        }}
      >
        {/* Title + trend */}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Portfolio Activity
            </Typography>
            {trendPct !== null && (
              <Chip
                label={`${trendPct >= 0 ? "↑" : "↓"} ${Math.abs(trendPct)}% vs prev period`}
                size="small"
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  color: trendPct >= 0 ? "#2e7d32" : "#e65100",
                  backgroundColor: trendPct >= 0 ? "rgba(46,125,50,0.1)" : "rgba(230,81,0,0.1)",
                  border: `1px solid ${trendPct >= 0 ? "rgba(46,125,50,0.25)" : "rgba(230,81,0,0.25)"}`,
                }}
              />
            )}
          </Box>
          {/* Stat pills */}
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary">
              <span style={{ fontWeight: 700, color: "#fff" }}>{grandTotal}</span> events in {period} days
            </Typography>
            {SERIES.map(({ key, label, color }) => (
              <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: color }} />
                <Typography variant="caption" color="text.disabled">
                  <span style={{ fontWeight: 700, color }}>{totals[key]}</span> {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Period selector */}
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          {([14, 30, 60] as Period[]).map((p) => (
            <Chip
              key={p}
              label={`${p}d`}
              size="small"
              onClick={() => setPeriod(p)}
              sx={{
                fontSize: "0.72rem",
                cursor: "pointer",
                fontWeight: 600,
                backgroundColor: period === p ? "primary.main" : "transparent",
                color: period === p ? "white" : "text.secondary",
                border: "1px solid",
                borderColor: period === p ? "primary.main" : "divider",
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ px: 1, pt: 1.5, pb: 0.5, height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
            <defs>
              {SERIES.map(({ key, color }) => (
                <linearGradient key={key} id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.5} />
                  <stop offset="95%" stopColor={color} stopOpacity={0.05} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.04)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "#616161" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#616161" }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              width={28}
            />
            <RechartTooltip
              content={<ActivityTooltip />}
              cursor={{ stroke: "rgba(255,255,255,0.1)", strokeWidth: 1 }}
            />
            {SERIES.map(({ key, label, color }) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                name={label}
                stroke={color}
                strokeWidth={1.5}
                fill={`url(#grad-${key})`}
                dot={false}
                activeDot={{ r: 3, fill: color, strokeWidth: 0 }}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      {/* Legend */}
      <Box
        sx={{
          px: 2.5,
          py: 1,
          display: "flex",
          gap: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "rgba(255,255,255,0.01)",
        }}
      >
        {SERIES.map(({ key, label, color }) => (
          <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 12, height: 8, borderRadius: 0.5, backgroundColor: color, opacity: 0.9 }} />
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

// ─── Priority account row (enhanced) ─────────────────────────────────────────

const PriorityAccountRow: React.FC<{
  match: AccountStreamMatch;
  rank: number;
  onOpen: () => void;
}> = ({ match, rank, onOpen }) => {
  const lastSignal = getLastSignalDate(match);
  const daysAgo = lastSignal ? daysBetween(lastSignal, new Date("2026-05-04")) : 999;
  const urgencyColor = daysAgo <= 3 ? "#e65100" : daysAgo <= 7 ? "#f9a825" : "#1976d2";
  const signalCount = match.signals.length;

  return (
    <Box
      onClick={onOpen}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.5,
        cursor: "pointer",
        borderLeft: `3px solid ${urgencyColor}`,
        "&:hover": { backgroundColor: "action.hover" },
        transition: "background-color 0.12s",
      }}
    >
      {/* Rank number */}
      <Typography
        variant="caption"
        fontWeight={800}
        sx={{ color: urgencyColor, minWidth: 16, flexShrink: 0, fontSize: "0.75rem" }}
      >
        {rank}
      </Typography>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {match.account.name}
        </Typography>
        {match.topAngle && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {match.topAngle.title}
          </Typography>
        )}
      </Box>

      {/* Signal badge + ICP + status */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        {signalCount > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.4,
              px: 0.75,
              py: 0.2,
              borderRadius: 0.75,
              backgroundColor: `${urgencyColor}15`,
              border: `1px solid ${urgencyColor}35`,
            }}
          >
            <SignalIcon sx={{ fontSize: 10, color: urgencyColor }} />
            <Typography variant="caption" sx={{ color: urgencyColor, fontWeight: 700, fontSize: "0.65rem" }}>
              {signalCount}
            </Typography>
          </Box>
        )}
        <Chip
          label={getICPLabel(match.streamId).split(" ").slice(0, 2).join(" ")}
          size="small"
          sx={{ fontSize: "0.62rem", height: 18, color: "text.disabled", maxWidth: 120 }}
        />
        <AccountStateChip state={match.status} />
      </Box>
    </Box>
  );
};

// ─── ICP coverage tile (full-width grid version) ─────────────────────────────

const SEGMENTS = [
  { key: "engaged" as const, label: "Engaged", color: "#2e7d32" },
  { key: "watching" as const, label: "Watching", color: "#1976d2" },
  { key: "candidate" as const, label: "Candidates", color: "#9e9e9e" },
  { key: "deprioritized" as const, label: "Deprioritized", color: "#424242" },
];

const ICPCoverageTile: React.FC<{
  streamId: string;
  onOpenPortfolio: (streamId: string) => void;
  onOpenStream: (streamId: string) => void;
}> = ({ streamId, onOpenPortfolio, onOpenStream }) => {
  const stream = MOCK_STREAMS.find((s) => s.id === streamId);
  if (!stream) return null;

  const { statusCounts, agentConfidence, icp, product } = stream;
  const total = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  const confidenceColor =
    agentConfidence === "high" ? "#2e7d32" : agentConfidence === "medium" ? "#f9a825" : "#616161";

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        overflow: "hidden",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": { borderColor: "primary.main" },
        transition: "border-color 0.15s",
      }}
    >
      {/* Header: name + count */}
      <Box sx={{ px: 2.5, pt: 2.25, pb: 1.5, display: "flex", alignItems: "flex-start", gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3} mb={0.5}>
            {icp.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: confidenceColor }} />
              <Typography variant="caption" sx={{ color: confidenceColor, textTransform: "capitalize" }}>
                {agentConfidence} agent confidence
              </Typography>
            </Box>
            <Typography variant="caption" color="text.disabled">·</Typography>
            <Typography variant="caption" color="text.secondary">
              {product.name}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Typography variant="h4" fontWeight={800} lineHeight={1} color="text.primary">
            {total}
          </Typography>
          <Typography variant="caption" color="text.disabled">accounts</Typography>
        </Box>
      </Box>

      {/* ICP definition summary */}
      <Box sx={{ px: 2.5, pb: 1.5, display: "flex", gap: 3, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="caption" color="text.disabled" fontWeight={700} display="block" mb={0.4}>
            ACCOUNT TYPES
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {icp.accountTypes.join(" · ")}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.disabled" fontWeight={700} display="block" mb={0.4}>
            INDUSTRIES
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {icp.industries.slice(0, 3).join(" · ")}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.disabled" fontWeight={700} display="block" mb={0.4}>
            GEOGRAPHY
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {icp.geography.map((g) => (
              <Chip key={g} label={g} size="small" variant="outlined" sx={{ fontSize: "0.65rem", height: 18 }} />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Distribution bar */}
      <Box sx={{ px: 2.5, pb: 1.75, flex: 1 }}>
        <Box sx={{ display: "flex", height: 12, borderRadius: 2, overflow: "hidden", gap: "2px", mb: 1.25 }}>
          {SEGMENTS.map(({ key, color }) => {
            const count = statusCounts[key];
            if (!count || !total) return null;
            return (
              <Tooltip key={key} title={`${count} ${key}`}>
                <Box sx={{ width: `${(count / total) * 100}%`, backgroundColor: color, minWidth: 4 }} />
              </Tooltip>
            );
          })}
        </Box>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {SEGMENTS.filter((s) => statusCounts[s.key] > 0).map(({ key, label, color }) => {
            const count = statusCounts[key];
            const pct = Math.round((count / total) * 100);
            return (
              <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary">
                  <span style={{ fontWeight: 700, color }}>{count}</span>
                  {" "}{label}
                  <span style={{ color: "#616161" }}> ({pct}%)</span>
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", borderTop: "1px solid", borderColor: "divider" }}>
        <Button
          size="small"
          fullWidth
          onClick={() => onOpenPortfolio(streamId)}
          sx={{ textTransform: "none", fontSize: "0.75rem", color: "text.secondary", borderRadius: 0, py: 1, "&:hover": { backgroundColor: "action.hover", color: "primary.main" } }}
        >
          View accounts
        </Button>
        <Divider orientation="vertical" flexItem />
        <Button
          size="small"
          fullWidth
          onClick={() => onOpenStream(streamId)}
          sx={{ textTransform: "none", fontSize: "0.75rem", color: "text.secondary", borderRadius: 0, py: 1, "&:hover": { backgroundColor: "action.hover", color: "primary.main" } }}
        >
          ICP detail
        </Button>
      </Box>
    </Paper>
  );
};

// ─── Mini feed item ───────────────────────────────────────────────────────────

const MiniFeedItem: React.FC<{
  item: ActivityFeedItem;
  onOpenAccount: (accountId: string, streamId: string) => void;
}> = ({ item, onOpenAccount }) => {
  const cfg = FEED_CONFIG[item.type];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.1,
        cursor: "pointer",
        "&:hover": { opacity: 0.85 },
      }}
      onClick={() => onOpenAccount(item.accountId, item.streamId)}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: cfg.color,
          flexShrink: 0,
          mt: 0.1,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {item.accountName}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {item.description}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.disabled" flexShrink={0}>
        {formatRelative(item.date)}
      </Typography>
    </Box>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

interface DashboardProps {
  onOpenFeed: () => void;
  onOpenPortfolio: (streamId?: string) => void;
  onOpenStream: (streamId: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onOpenFeed, onOpenPortfolio, onOpenStream }) => {
  const [selectedMatch, setSelectedMatch] = useState<AccountStreamMatch | null>(null);

  const allMatches = MOCK_ACCOUNT_STREAM_MATCHES;

  const priorityMatches = useMemo(
    () =>
      [...allMatches]
        .filter((m) => m.status !== "candidate" && m.status !== "deprioritized" && m.signals.length > 0)
        .sort((a, b) => {
          const da = getLastSignalDate(a)?.getTime() ?? 0;
          const db = getLastSignalDate(b)?.getTime() ?? 0;
          return db - da;
        })
        .slice(0, 5),
    [allMatches],
  );

  const withNewSignals = allMatches.filter((m) => {
    const d = getLastSignalDate(m);
    return d ? Date.now() - d.getTime() < 7 * 86400000 : false;
  }).length;

  const engagedCount = allMatches.filter((m) => m.status === "engaged").length;
  const candidateCount = allMatches.filter((m) => m.status === "candidate").length;
  const totalAngles = allMatches.reduce((sum, m) => sum + m.angles.length, 0);

  const feedPreview = MOCK_ACTIVITY_FEED.slice(0, 5);

  const openAccount = (accountId: string, streamId: string): void => {
    const match = allMatches.find((m) => m.account.id === accountId && m.streamId === streamId);
    if (match) setSelectedMatch(match);
  };

  return (
    <Box sx={{ height: "100%", overflowY: "auto" }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "baseline",
          gap: 2,
        }}
      >
        <Typography variant="h5" fontWeight={800}>
          Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          4 May 2026
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* ── Activity timeline (full width hero) ──────────────────────────── */}
          <ActivityTimeline />

          {/* ── Metric cards ─────────────────────────────────────────────────── */}
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <MetricCard
              value={withNewSignals}
              label="New signals"
              sublabel="Last 7 days"
              color="#f9a825"
              icon={<SignalIcon />}
              onClick={onOpenFeed}
            />
            <MetricCard
              value={engagedCount}
              label="Engaged"
              sublabel="Actively pursuing"
              color="#2e7d32"
              icon={<EngagedIcon />}
              onClick={() => onOpenPortfolio()}
            />
            <MetricCard
              value={candidateCount}
              label="Candidates"
              sublabel="Awaiting review"
              color="#9e9e9e"
              icon={<PeopleIcon />}
              onClick={() => onOpenPortfolio()}
            />
            <MetricCard
              value={totalAngles}
              label="Opportunity angles"
              sublabel="Identified by agent"
              color="#1976d2"
              icon={<AgentIcon />}
              onClick={onOpenFeed}
            />
          </Box>

          {/* ── ICP Coverage — full width tile grid ──────────────────────────── */}
          <Box>
            <Typography variant="caption" color="text.disabled" fontWeight={700} display="block" mb={1.25}>
              ICP COVERAGE
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              {MOCK_STREAMS.map((s) => (
                <Grid item xs={12} md={6} key={s.id} sx={{ display: "flex", flexDirection: "column" }}>
                  <ICPCoverageTile
                    streamId={s.id}
                    onOpenPortfolio={onOpenPortfolio}
                    onOpenStream={onOpenStream}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* ── Two-column: accounts + feed ──────────────────────────────────── */}
          <Box sx={{ display: "flex", gap: 2.5, alignItems: "stretch", flexWrap: "wrap" }}>
            {/* Priority accounts */}
            <Box sx={{ flex: "1 1 55%", minWidth: 280, display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.25 }}>
                <Typography variant="caption" color="text.disabled" fontWeight={700}>
                  ACCOUNTS WITH RECENT SIGNALS
                </Typography>
                <Button
                  size="small"
                  endIcon={<ChevronRightIcon fontSize="inherit" />}
                  onClick={() => onOpenPortfolio()}
                  sx={{ textTransform: "none", fontSize: "0.72rem", color: "text.secondary" }}
                >
                  Portfolio
                </Button>
              </Box>
              <Paper
                elevation={0}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, overflow: "hidden", flex: 1 }}
              >
                {priorityMatches.length === 0 ? (
                  <Box sx={{ p: 3 }}>
                    <Typography variant="body2" color="text.disabled">
                      No accounts with recent signals.
                    </Typography>
                  </Box>
                ) : (
                  priorityMatches.map((m, i) => (
                    <React.Fragment key={m.id}>
                      <PriorityAccountRow match={m} rank={i + 1} onOpen={() => setSelectedMatch(m)} />
                      {i < priorityMatches.length - 1 && (
                        <Divider sx={{ ml: "calc(16px + 16px + 8px)" }} />
                      )}
                    </React.Fragment>
                  ))
                )}
              </Paper>
            </Box>

            {/* Recent activity feed */}
            <Box sx={{ flex: "1 1 30%", minWidth: 240, display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.25 }}>
                <Typography variant="caption" color="text.disabled" fontWeight={700}>
                  RECENT ACTIVITY
                </Typography>
                <Button
                  size="small"
                  endIcon={<ChevronRightIcon fontSize="inherit" />}
                  onClick={onOpenFeed}
                  sx={{ textTransform: "none", fontSize: "0.72rem", color: "text.secondary" }}
                >
                  View feed
                </Button>
              </Box>
              <Paper
                elevation={0}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, px: 2, flex: 1 }}
              >
                {feedPreview.map((item, i) => (
                  <React.Fragment key={item.id}>
                    <MiniFeedItem item={item} onOpenAccount={openAccount} />
                    {i < feedPreview.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Paper>
            </Box>
          </Box>
        </Stack>
      </Box>

      <AccountDetailDrawer match={selectedMatch} onClose={() => setSelectedMatch(null)} />
    </Box>
  );
};

export default Dashboard;
